<!-- C:\platforma-frontend-admin-creator\app-subdomain-creators\src\layouts\CreatorLayout.vue -->

<template>
  <div class="creator-shell-backdrop">
    <PlatformaUpdateBanner />
    <GlobalNotificationToast />
    <CreatorBillingGuard />
    <CreatorMemberJoinedCongratsModal
      v-model:open="memberJoinedModalOpen"
      :member-name="memberJoinedModalName"
      :link-path="memberJoinedModalLinkPath"
      @view-member="handleMemberJoinedViewMember"
    />
    <TransitionRoot as="template" :show="platformInitiationOpen">
      <Dialog as="div" class="relative z-[2600]" @close="dismissPlatformInitiation">
        <div
          class="fixed inset-0 bg-slate-950/70 backdrop-blur-sm transition-opacity duration-500"
          :class="platformInitiationStage === 'leaving' ? 'opacity-0' : 'opacity-100'"
        />
        <div class="fixed inset-0 overflow-y-auto p-4">
          <div class="flex min-h-full items-center justify-center">
            <DialogPanel
              class="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl transition duration-500"
              :class="platformInitiationStage === 'leaving' ? 'scale-[1.03] opacity-0' : 'scale-100 opacity-100'"
            >
              <template v-if="platformInitiationStage === 'prompt'">
                <div class="bg-gradient-to-br from-violet-600 via-blue-500 to-emerald-400 px-7 py-8 text-white sm:px-10">
                  <div class="flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 ring-1 ring-white/30">
                    <RocketLaunchIcon class="h-9 w-9" aria-hidden="true" />
                  </div>
                  <p class="mt-6 text-xs font-semibold uppercase tracking-[0.22em] text-white/75">Your Platforma member area</p>
                  <DialogTitle class="mt-2 text-3xl font-semibold leading-tight">Initiate Your New Online Member Platform</DialogTitle>
                  <p class="mt-3 max-w-lg text-sm leading-6 text-white/85">Platforma will create your dedicated member area and prepare it for customization.</p>
                </div>
                <div class="px-7 py-7 sm:px-10">
                  <p class="text-sm leading-6 text-slate-600">Once initialized, you can choose your theme, create pages, configure memberships, add products, and invite members. We only allocate these resources after you confirm.</p>
                  <p v-if="platformInitiationError" class="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ platformInitiationError }}</p>
                  <p v-if="platformInitiationMessage" class="mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">{{ platformInitiationMessage }}</p>
                  <div class="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button type="button" class="rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" :disabled="platformInitiating" @click="dismissPlatformInitiation">Not yet</button>
                    <button type="button" class="rounded-xl bg-[#2D8CFF] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/20 hover:bg-[#1B67C2] disabled:cursor-not-allowed disabled:opacity-60" :disabled="platformInitiating" @click="initiateCreatorPlatform">
                      Initiate My Member Platform
                    </button>
                  </div>
                </div>
              </template>

              <div v-else class="platform-provisioning-screen relative isolate overflow-hidden px-7 py-12 text-center sm:px-12 sm:py-14">
                <div class="platform-provisioning-orb platform-provisioning-orb--one" aria-hidden="true" />
                <div class="platform-provisioning-orb platform-provisioning-orb--two" aria-hidden="true" />
                <div class="relative z-10 flex flex-col items-center">
                  <div
                    class="platform-stack-stage"
                    :class="{ 'platform-stack-stage--complete': platformInitiationComplete }"
                    aria-hidden="true"
                  >
                    <span class="platform-stack-glow" />
                    <span class="platform-stack-ring platform-stack-ring--one" />
                    <span class="platform-stack-ring platform-stack-ring--two" />
                    <span class="platform-stack-layer platform-stack-layer--bottom"><img :src="platformaStackAsset" alt=""></span>
                    <span class="platform-stack-layer platform-stack-layer--middle"><img :src="platformaStackAsset" alt=""></span>
                    <span class="platform-stack-layer platform-stack-layer--top"><img :src="platformaStackAsset" alt=""></span>
                    <span v-if="platformInitiationComplete" class="platform-stack-check">✓</span>
                  </div>
                  <p class="mt-7 text-xs font-bold uppercase tracking-[0.24em] text-blue-600">
                    {{ platformInitiationComplete ? 'Platform ready' : 'Building your Platforma' }}
                  </p>
                  <DialogTitle class="mt-3 text-3xl font-semibold tracking-tight text-slate-900">
                    {{ platformInitiationComplete ? 'Your member platform is ready!' : 'Creating your new online home' }}
                  </DialogTitle>
                  <p class="mt-3 min-h-[48px] max-w-md text-sm leading-6 text-slate-600" aria-live="polite">
                    {{ platformInitiationProgressMessage }}
                  </p>
                  <div class="mt-6 flex w-full max-w-xs items-center gap-2" aria-hidden="true">
                    <span
                      v-for="step in 4"
                      :key="step"
                      class="h-1.5 flex-1 rounded-full transition-all duration-500"
                      :class="step <= platformInitiationProgressIndex ? 'bg-[#2D8CFF]' : 'bg-slate-200'"
                    />
                  </div>
                  <p v-if="!platformInitiationComplete" class="mt-5 text-xs text-slate-400">Please keep this window open for a moment.</p>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
    <TransitionRoot
      as="template"
      :show="sidebarOpen"
    >
      <Dialog
        class="relative z-50 lg:hidden"
        @close="sidebarOpen = false"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                  <button
                    type="button"
                    class="-m-2.5 p-2.5"
                    @click="sidebarOpen = false"
                  >
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon
                      class="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </TransitionChild>

              <div
                class="app-scroll flex grow flex-col gap-y-5 overflow-y-auto border-r border-white/60 bg-white/65 px-6 pb-4 backdrop-blur-2xl"
              >
                <div class="flex h-16 shrink-0 items-center">
                  <img
                    class="h-8 w-auto"
                    src="@/assets/media/images/platforma-logo-in-color.svg"
                    alt="Platforma Logo"
                  >
                </div>
                <nav class="flex flex-1 flex-col">
                  <ul
                    role="list"
                    class="flex flex-1 flex-col gap-y-7"
                  >
                    <li v-if="onlinePlatformNavigation.length">
                      <ul
                        role="list"
                        class="-mx-2 space-y-1"
                      >
                        <li
                          v-for="item in navigation"
                          :key="item.name"
                        >
                          <router-link
                            :to="item.to"
                            :data-creator-tour="item.to === '/creator/dashboard' ? 'dashboard-nav' : null"
                            class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 hover:bg-gray-50"
                            :class="{'text-[#2d8bff] bg-gray-50': currentRoute === item.to, 'text-gray-700': currentRoute !== item.to}"
                            @click="setActiveRoute(item.to)"
                          >
                            <component
                              :is="item.icon"
                              :class="[currentRoute === item.to ? 'text-[#2d8bff]' : 'text-gray-400 group-hover:text-[#2d8bff]', 'h-6 w-6 shrink-0']"
                              aria-hidden="true"
                            />
                            {{ item.name }}
                          </router-link>
                        </li>
                      </ul>
                    </li>

                    <!-- Online Platform Dropdown -->
                    <li>
                      <button
                        class="flex w-full gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-[#2d8bff]"
                        @click="toggleOnlinePlatformMenu"
                      >
                        <HomeIcon
                          class="h-6 w-6 text-gray-400 group-hover:text-[#2d8bff]"
                          aria-hidden="true"
                        />
                        Online Platform
                      </button>
                      <ul
                        v-if="isOnlinePlatformMenuOpen"
                        role="list"
                        class="ml-4 space-y-1"
                      >
                        <li
                          v-for="item in onlinePlatformNavigation"
                          :key="item.name"
                        >
                          <router-link
                            :to="item.to"
                            :data-creator-tour="item.isThemeEditor ? 'theme-editor' : item.name === 'Membership Settings' ? 'membership-settings' : null"
                            class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 hover:bg-gray-50"
                            :class="{'text-[#2d8bff] bg-gray-50': currentRoute === item.to, 'text-gray-700': currentRoute !== item.to}"
                            @click="setActiveRoute(item.to)"
                          >
                            <component
                              :is="item.icon"
                              :class="[currentRoute === item.to ? 'text-[#2d8bff]' : 'text-gray-400 group-hover:text-[#2d8bff]', 'h-6 w-6 shrink-0']"
                              aria-hidden="true"
                            />
                            {{ item.name }}
                          </router-link>
                        </li>
                      </ul>
                    </li>

                    <li class="mt-auto">
                      <a
                        href="#"
                        class="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-[#2d8bff]"
                      >
                        <Cog6ToothIcon
                          class="h-6 w-6 shrink-0 text-gray-400 group-hover:text-[#2d8bff]"
                          aria-hidden="true"
                        />
                        Settings
                      </a>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Static sidebar for desktop -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
      <div
        class="app-scroll flex grow flex-col gap-y-5 overflow-y-auto border-r border-white/60 bg-transparent px-6 pb-4 backdrop-blur-xl"
      >
        <div class="flex h-16 shrink-0 items-center">
          <img
            class="h-8 w-auto"
            src="@/assets/media/images/platforma-logo-in-color.svg"
            alt="Platforma Logo"
          >
        </div>
        <nav class="flex flex-1 flex-col">
          <ul
            role="list"
            class="flex flex-1 flex-col gap-y-7"
          >
            <li v-if="engageNavigation.length">
              <ul
                role="list"
                class="-mx-2 space-y-1"
              >
                <li
                  v-for="item in navigation"
                  :key="item.name"
                >
                  <router-link
                    :to="item.to"
                    :data-creator-tour="item.to === '/creator/dashboard' ? 'dashboard-nav' : null"
                    class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 hover:bg-gray-50"
                    :class="{'text-[#2d8bff] bg-gray-50': currentRoute === item.to, 'text-gray-700': currentRoute !== item.to}"
                    @click="setActiveRoute(item.to)"
                  >
                    <component
                      :is="item.icon"
                      :class="[currentRoute === item.to ? 'text-[#2d8bff]' : 'text-gray-400 group-hover:text-[#2d8bff]', 'h-6 w-6 shrink-0']"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- Engage Dropdown for Desktop -->
            <li v-if="onlinePlatformNavigation.length">
              <button
                class="flex w-full gap-x-3 rounded-md py-2 pl-1 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-[#2d8bff]"
                @click="toggleEngageMenu"
              >
                <RocketLaunchIcon class="h-6 w-auto text-gray-400" />
                Engage
              </button>
              <ul
                v-if="isEngageMenuOpen"
                role="list"
                class="ml-4 space-y-2"
              >
                <li
                  v-for="item in engageNavigation"
                  :key="item.name"
                >
                  <router-link
                    :to="item.to"
                    class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 hover:bg-gray-50"
                    :class="{'text-[#2d8bff] bg-gray-50': currentRoute === item.to, 'text-gray-700': currentRoute !== item.to}"
                    @click="setActiveRoute(item.to)"
                  >
                    <component
                      :is="item.icon"
                      :class="[currentRoute === item.to ? 'text-[#2d8bff]' : 'text-gray-400 group-hover:text-[#2d8bff]', 'h-6 w-6 shrink-0']"
                      aria-hidden="true"
                    />
                    {{ item.name }}
                  </router-link>
                </li>
              </ul>
            </li>

            <!-- Online Platform Dropdown for Desktop -->
            <li>
              <button
                class="flex w-full gap-x-3 rounded-md py-2 pl-1 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 hover:text-[#2d8bff]"
                @click="toggleOnlinePlatformMenu"
              >
                <img
                  class="h-6 w-auto rounded-full"
                  src="@/assets/media/images/platforma-stack.png"
                  alt="Platforma Logo"
                >
                Online Platform
              </button>
              <ul
                v-if="isOnlinePlatformMenuOpen"
                role="list"
                class="ml-4 space-y-2"
              >
                <li
                  v-for="item in onlinePlatformNavigation"
                  :key="item.name"
                >
                  <router-link
                    :to="item.to"
                    :data-creator-tour="item.isThemeEditor ? 'theme-editor' : item.name === 'Membership Settings' ? 'membership-settings' : null"
                    class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 hover:bg-gray-50"
                    :class="{'text-[#2d8bff] bg-gray-50': currentRoute === item.to, 'text-gray-700': currentRoute !== item.to}"
                    @click="setActiveRoute(item.to)"
                  >
                    <component
                      :is="item.icon"
                      :class="[currentRoute === item.to ? 'text-[#2d8bff]' : 'text-gray-400 group-hover:text-[#2d8bff]', 'h-6 w-6 shrink-0']"
                      aria-hidden="true"
                    />
                    {{ item.name }}

                    <EyeIcon
                      v-if="item.isThemeEditor"
                      class="h-6 w-6 ml-auto"
                      aria-hidden="true"
                    />
                  </router-link>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div
      class="app-scroll h-screen overflow-y-scroll bg-transparent lg:pl-72"
      data-creator-main-scroll
    >
      <div
        class="sticky top-0 z-50 flex h-14 shrink-0 items-center gap-x-4 border-b border-white/60 bg-transparent px-4 shadow-sm backdrop-blur-xl sm:gap-x-6 sm:px-6 lg:px-8"
      >
        <button
          type="button"
          class="-m-2.5 p-2.5 text-gray-700 lg:hidden"
          @click="sidebarOpen = true"
        >
          <span class="sr-only">Open sidebar</span>
          <Bars3Icon
            class="h-6 w-6"
            aria-hidden="true"
          />
        </button>

        <!-- Separator -->
        <div
          class="h-6 w-px bg-gray-200 lg:hidden"
          aria-hidden="true"
        />

        <div class="flex flex-1 items-center justify-end gap-x-4 lg:gap-x-6">
          <div class="flex items-center gap-x-4 lg:gap-x-6">
            <NotificationBellMenuCreator
              v-if="canAccessNotifications"
              :unread-count="creatorUnreadCount"
              :unread-count-label="creatorUnreadCountLabel"
            />

            <div
              class="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200"
              aria-hidden="true"
            />

            <!-- Profile dropdown -->
            <Menu
              v-slot="{ open }"
              as="div"
              class="relative z-[1200]"
            >
              <div class="relative group/account inline-flex items-center ">
                <MenuButton class="-m-1.5 flex items-center p-1.5">
                  <span class="sr-only">Open user menu</span>
                  <div class="relative h-10 w-10 rounded-full bg-zinc-100">
                    <div
                      v-if="isLoading"
                      class="h-10 w-10 rounded-full bg-zinc-300 animate-pulse"
                    />
                    <img
                      v-else
                      :src="profilePicSrc"
                      class="h-10 w-10 rounded-full bg-gray-50 object-cover"
                      alt="Creator Avatar"
                      @error="handleProfileImageError"
                    >
                    <div
                      class="absolute -bottom-[10px] -right-[7px] flex h-5 w-5 items-center justify-center rounded-full border-[3px] border-white bg-zinc-300"
                    >
                      <ChevronDownIcon class="h-4 w-4 text-zinc-900" />
                    </div>
                  </div>
                </MenuButton>
                <p
                  v-if="!open"
                  class="pointer-events-none absolute left-1/2 top-12 z-[2200] -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover/account:opacity-100 group-hover/account:translate-y-0"
                >
                  Account
                </p>
              </div>
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems class="absolute right-0 z-[2220] mt-3.5 w-56 origin-top-right rounded-xl bg-white p-2 shadow-lg ring-1 ring-gray-900/10">
                  <div class="mb-2 flex items-center gap-3 rounded-lg px-2 py-2">
                    <div class="h-10 w-10 overflow-hidden rounded-full bg-zinc-100">
                      <img
                        :src="profilePicSrc"
                        class="h-10 w-10 rounded-full bg-gray-50 object-cover"
                        alt="Creator Avatar"
                        @error="handleProfileImageError"
                      >
                    </div>
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold text-zinc-900">
                        {{ creatorFullName || (isStaffAccount ? 'Staff User' : 'Creator Admin') }}
                      </p>
                      <p class="text-xs text-zinc-500">
                        {{ accountRoleLabel }}
                      </p>
                      <p
                        v-if="isStaffAccount && creatorOwnerLabel"
                        class="truncate text-[11px] text-zinc-400"
                      >
                        Admin: {{ creatorOwnerLabel }}
                      </p>
                    </div>
                  </div>
                  <div class="mb-2 h-px bg-zinc-200" />
                  <MenuItem
                    v-if="canAccessAccountSettings"
                    v-slot="{ active }"
                  >
                    <router-link
                      :to="accountSettingsPath"
                      class="block rounded-lg px-3 py-2 text-sm font-medium transition"
                      :class="active ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700'"
                    >
                      Account
                    </router-link>
                  </MenuItem>
                  <MenuItem
                    v-if="canAccessSupport"
                    v-slot="{ active }"
                  >
                    <router-link
                      to="/creator/support"
                      class="block rounded-lg px-3 py-2 text-sm font-medium transition"
                      :class="active ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700'"
                    >
                      Support
                    </router-link>
                  </MenuItem>
                  <MenuItem v-slot="{ active }">
                    <button
                      type="button"
                      class="block w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition"
                      :class="active ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-700'"
                      @click="handleLogout"
                    >
                      Sign Out
                    </button>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
        </div>
      </div>

      <main class="py-10">
        <div class="creator-route-stage px-4 sm:px-6 lg:px-8">
          <router-view v-slot="{ Component, route: viewRoute }">
            <transition
              enter-active-class="transition-[opacity,transform,filter] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
              enter-from-class="translate-x-5 opacity-0 blur-[2px]"
              enter-to-class="translate-x-0 opacity-100 blur-none"
              leave-active-class="pointer-events-none absolute inset-0 z-0 w-full transition-[opacity,transform,filter] duration-200 ease-out"
              leave-from-class="translate-x-0 opacity-100 blur-none"
              leave-to-class="-translate-x-2 opacity-0 blur-[1px]"
            >
              <div :key="creatorPageTransitionKey(viewRoute)" class="creator-page-view">
                <component :is="Component" />
              </div>
            </transition>
          </router-view>
        </div>
      </main>

      <!-- Global Upload Queue (always visible) -->
      <MediaUploadQueue />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, getCurrentInstance, watch, computed, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,

  
} from '@headlessui/vue'
import {
  Bars3Icon,
  Cog6ToothIcon,
  HomeIcon,
  UsersIcon,
  XMarkIcon,
  ChatBubbleBottomCenterTextIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  EyeIcon,
  ChartBarIcon,
  VideoCameraIcon,
  PaperAirplaneIcon,
  MegaphoneIcon,
  RocketLaunchIcon,
  PhotoIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  ShoppingCartIcon,
  BuildingStorefrontIcon,
  PhoneArrowUpRightIcon,
  AcademicCapIcon,
  BookOpenIcon,
  FilmIcon,
  GlobeAltIcon,
  MicrophoneIcon,
  NewspaperIcon,
  QueueListIcon,
  Squares2X2Icon,
} from '@heroicons/vue/24/outline'
import { ChevronDownIcon } from '@heroicons/vue/20/solid'
import defaultAvatarAsset from '@/assets/media/images/default-avatar.png';
import platformaStackAsset from '@/assets/media/images/platformalogostack.png';
import MediaUploadQueue from '@/Components/MediaUploadQueue.vue'
import GlobalNotificationToast from '@/Components/GlobalNotificationToast.vue'
import PlatformaUpdateBanner from '@/Components/PlatformaUpdateBanner.vue'
import CreatorBillingGuard from '@/Components/CreatorBillingGuard.vue'
import NotificationBellMenuCreator from '@/Components/NotificationBellMenuCreator.vue'
import CreatorMemberJoinedCongratsModal from '@/Components/CreatorMemberJoinedCongratsModal.vue'

import {
  fetchCreatorStaffSession,
  canAccessCreatorPath,
  getAllowedStockroomTabsForFeature,
} from '@/utils/creatorStaffAccess';
import { useFeatureStore } from '@/stores/featureStore';
// 🔌 NEW: bring in websocket store + sound
import { useWebsocketStore } from '@/stores/websocketStore';
import { usePresenceStore } from '@/stores/presenceStore';
import incomingMessageSound from '@/assets/media/sounds/platforma_wire_chat_sound.mp3';
import storefrontSellSound from '@/assets/media/sounds/sell-sound.mp3';
import btcIconUrl from '@/assets/media/images/btc_icon_Platforma_Icons.svg';

const router = useRouter()
const route = useRoute()
const loginTransition = inject('creatorLoginTransition', null)
const CREATOR_AUTH_EXPIRED_EVENT = 'creator:auth-expired';
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const platformInitiationOpen = ref(false);
const platformInitiating = ref(false);
const platformInitiationStage = ref('prompt');
const platformInitiationError = ref('');
const platformInitiationMessage = ref('');
const platformInitiationProgressIndex = ref(1);
const platformInitiationProgressMessage = ref('Allocating your secure member workspace…');
const platformInitiationComplete = computed(() => ['ready', 'leaving'].includes(platformInitiationStage.value));
const platformInitiationProgressMessages = [
  'Allocating your secure member workspace…',
  'Connecting your creator tools and member experience…',
  'Preparing your Theme Editor and starter settings…',
];
let platformInitiationProgressTimer = null;
let platformInitiationRunId = 0;
let platformInitiationLookupId = 0;

const platformContinueDestination = () => {
  const continuePath = String(route.query.continue || '').trim();
  return continuePath.startsWith('/creator/') ? continuePath : '/creator/theme-editor';
};

const fetchPlatformStatus = async () => {
  const response = await fetch(`${apiProxyUrl}/api/creator-platform/status`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || 'Unable to confirm your member platform status. Please try again.');
  }
  if (!payload?.platform || typeof payload.platform.isProvisioned !== 'boolean') {
    throw new Error('Member platform status is incomplete. Please try again.');
  }
  return payload?.platform || null;
};

const waitForPlatformAnimation = (milliseconds) => new Promise((resolve) => {
  window.setTimeout(resolve, milliseconds);
});

const stopPlatformInitiationProgress = () => {
  if (!platformInitiationProgressTimer) return;
  window.clearInterval(platformInitiationProgressTimer);
  platformInitiationProgressTimer = null;
};

const startPlatformInitiationProgress = () => {
  stopPlatformInitiationProgress();
  platformInitiationProgressIndex.value = 1;
  platformInitiationProgressMessage.value = platformInitiationProgressMessages[0];
  let messageIndex = 0;
  platformInitiationProgressTimer = window.setInterval(() => {
    messageIndex = Math.min(messageIndex + 1, platformInitiationProgressMessages.length - 1);
    platformInitiationProgressIndex.value = Math.min(messageIndex + 1, 3);
    platformInitiationProgressMessage.value = platformInitiationProgressMessages[messageIndex];
  }, 950);
};

const openPlatformInitiation = () => {
  stopPlatformInitiationProgress();
  platformInitiationStage.value = 'prompt';
  platformInitiationError.value = '';
  platformInitiationMessage.value = '';
  platformInitiationProgressIndex.value = 1;
  platformInitiationProgressMessage.value = platformInitiationProgressMessages[0];
  platformInitiationOpen.value = true;
};

const resolvePlatformInitiationRequest = async () => {
  if (String(route.query.provisionPlatform || '') !== '1') return;
  const requestedPath = route.fullPath;
  const lookupId = ++platformInitiationLookupId;
  let platform;
  try {
    platform = await fetchPlatformStatus();
  } catch (error) {
    if (lookupId !== platformInitiationLookupId || route.fullPath !== requestedPath) return;
    platformInitiationOpen.value = false;
    if (typeof notify === 'function') notify({ title: 'Member area status unavailable', body: error?.message || 'Please try again.', variant: 'error' });
    await cleanPlatformInitiationQuery();
    return;
  }
  if (lookupId !== platformInitiationLookupId || route.fullPath !== requestedPath) return;
  if (platform.isProvisioned) {
    platformInitiationOpen.value = false;
    await router.replace(platformContinueDestination());
    return;
  }
  const session = await fetchCreatorStaffSession();
  if (lookupId !== platformInitiationLookupId || route.fullPath !== requestedPath) return;
  if (session?.isStaff || !session?.creator?.userId) {
    if (typeof notify === 'function') notify({ title: 'Creator setup required', body: session?.isStaff ? 'The creator account owner must initiate the member area.' : 'Please refresh and try again when your creator session is available.', variant: 'error' });
    await cleanPlatformInitiationQuery();
    return;
  }
  openPlatformInitiation();
};

const cleanPlatformInitiationQuery = async () => {
  const query = { ...route.query };
  delete query.provisionPlatform;
  delete query.continue;
  await router.replace({ path: route.path, query });
};

const dismissPlatformInitiation = async () => {
  if (platformInitiating.value) return;
  platformInitiationRunId += 1;
  stopPlatformInitiationProgress();
  platformInitiationOpen.value = false;
  platformInitiationStage.value = 'prompt';
  platformInitiationError.value = '';
  platformInitiationMessage.value = '';
  if (route.query.provisionPlatform) await cleanPlatformInitiationQuery();
};

const waitForProvisionedPlatform = async (initialPlatform, runId) => {
  if (initialPlatform?.isProvisioned) return initialPlatform;
  const deadline = Date.now() + 60000;
  while (Date.now() < deadline) {
    await waitForPlatformAnimation(1250);
    if (runId !== platformInitiationRunId) return null;
    const response = await fetch(`${apiProxyUrl}/api/creator-platform/status`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) {
      throw new Error(payload?.message || 'Unable to confirm your member platform status.');
    }
    if (payload?.platform?.isProvisioned) return payload.platform;
    if (String(payload?.platform?.status || '').toLowerCase() === 'failed') {
      throw new Error(payload?.platform?.lastError || 'Your member platform could not be prepared. Please try again.');
    }
  }
  throw new Error('Your member platform is taking longer than expected. Please try again in a moment.');
};

const initiateCreatorPlatform = async () => {
  if (platformInitiating.value) return;
  const runId = ++platformInitiationRunId;
  const animationStartedAt = Date.now();
  platformInitiating.value = true;
  platformInitiationError.value = '';
  platformInitiationMessage.value = '';
  try {
    const existingPlatform = await fetchPlatformStatus();
    if (runId !== platformInitiationRunId) return;
    if (existingPlatform?.isProvisioned) {
      platformInitiationProgressIndex.value = 4;
      platformInitiationProgressMessage.value = 'Your member area is already active. Opening Theme Editor now…';
      platformInitiationStage.value = 'ready';
      window.dispatchEvent(new CustomEvent('platforma:creator-platform-activated', { detail: existingPlatform }));
      await waitForPlatformAnimation(650);
      if (runId !== platformInitiationRunId) return;
      await router.replace(platformContinueDestination());
      platformInitiationOpen.value = false;
      return;
    }

    platformInitiationStage.value = 'building';
    startPlatformInitiationProgress();
    const response = await fetch(`${apiProxyUrl}/api/creator-platform/activate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ confirmation: 'INITIATE' }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Unable to initiate your member platform.');
    const provisionedPlatform = await waitForProvisionedPlatform(payload?.platform, runId);
    if (!provisionedPlatform || runId !== platformInitiationRunId) return;

    const minimumAnimationRemaining = Math.max(0, 3200 - (Date.now() - animationStartedAt));
    if (minimumAnimationRemaining) await waitForPlatformAnimation(minimumAnimationRemaining);
    if (runId !== platformInitiationRunId) return;

    stopPlatformInitiationProgress();
    platformInitiationProgressIndex.value = 4;
    platformInitiationProgressMessage.value = 'Everything is connected. Opening your Theme Editor now…';
    platformInitiationStage.value = 'ready';
    await waitForPlatformAnimation(900);
    if (runId !== platformInitiationRunId) return;

    const destination = platformContinueDestination();
    window.dispatchEvent(new CustomEvent('platforma:creator-platform-activated', { detail: provisionedPlatform }));
    await router.replace(destination);
    platformInitiationStage.value = 'leaving';
    await waitForPlatformAnimation(500);
    if (runId !== platformInitiationRunId) return;
    platformInitiationOpen.value = false;
  } catch (error) {
    stopPlatformInitiationProgress();
    platformInitiationStage.value = 'prompt';
    platformInitiationError.value = error?.message || 'Unable to initiate your member platform.';
  } finally {
    if (runId === platformInitiationRunId) platformInitiating.value = false;
  }
};
const app = getCurrentInstance();
const notify = app?.appContext?.config?.globalProperties?.$notify;

// 🔌 WebSocket store
const websocketStore = useWebsocketStore();
const presenceStore = usePresenceStore(); 
const featureStore = useFeatureStore();

let unsubscribePresenceSnapshot = null;   // 🔥
let unsubscribePresenceUpdate = null;     // 🔥

let unsubscribeChatNewMessage = null;
let unsubscribeNewsfeedPostCreated = null;
let unsubscribeNewsfeedCommentToast = null;
let unsubscribeNewsfeedReplyToast = null;
let unsubscribeNewsfeedPostReactionToast = null;
let unsubscribeNewsfeedCommentReactionToast = null;
let unsubscribeVideoChannelPublished = null;
let unsubscribeVideoChannelPublishedToast = null;
let unsubscribeFlixTheatrePublished = null;
let unsubscribeFlixTheatrePublishedToast = null;
let unsubscribeStorefrontOrderConfirmedToast = null;
let unsubscribeCryptoReceiveToast = null;
let unsubscribeMemberLifecycle = null;
let unsubscribeMemberJoinedToast = null;
let unsubscribeMemberSubscriptionToast = null;
let unsubscribeMeetNotificationToast = null;
const seenNewsfeedToastPostIds = new Set();
const seenVideoChannelToastIds = new Set();
const seenFlixTheatreToastIds = new Set();
const seenStorefrontOrderToastIds = new Set();
const seenCryptoReceiveToastIds = new Set();
const seenMemberJoinedToastIds = new Set();
const seenMemberSubscriptionToastIds = new Set();
const seenMeetNotificationToastIds = new Set();
const newsfeedInstanceNameCache = new Map();
let unsubscribeNotificationsRefresh = null;
const creatorUnreadCount = ref();
const creatorUnreadCountLabel = ref('0');
let creatorUnreadPollTimer = null;
let creatorUnreadSyncListener = null;
let creatorVisibilityListener = null;
let creatorAuthExpiredListener = null;
const creatorExitInProgress = ref(false);
// 🔊 global incoming message sound
let incomingMessageAudio = null;
let storefrontOrderConfirmedAudio = null;
const memberJoinedModalOpen = ref(false);
const memberJoinedModalName = ref('');
const memberJoinedModalLinkPath = ref('');

const initIncomingMessageAudio = () => {
  if (incomingMessageAudio) return;
  incomingMessageAudio = new Audio(incomingMessageSound);
  incomingMessageAudio.preload = 'auto';
};

const initStorefrontOrderConfirmedAudio = () => {
  if (storefrontOrderConfirmedAudio) return;
  storefrontOrderConfirmedAudio = new Audio(storefrontSellSound);
  storefrontOrderConfirmedAudio.preload = 'auto';
};

const toMemberLifecycleEventKey = (payload = {}) => {
  const notificationSlug = String(payload?.notificationSlug || '').trim();
  if (notificationSlug) return `slug:${notificationSlug}`;
  const event = String(payload?.event || '').trim().toLowerCase();
  const memberUserId = Number(payload?.memberUserId || 0);
  const leadPublicId = String(payload?.leadPublicId || '').trim();
  const ts = String(payload?.ts || '').trim();
  return [event, memberUserId || '', leadPublicId, ts].join(':');
};

const emitMemberLifecycleUpdate = (payload = {}) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('creator:member-lifecycle', { detail: payload }));
};

const handleMemberJoinedViewMember = (path = '') => {
  const nextPath = String(path || '').trim();
  if (!nextPath) return;
  router.push(nextPath);
};

const openMemberJoinedModal = (payload = {}) => {
  memberJoinedModalName.value = String(
    payload?.memberName || payload?.memberEmail || 'New Member'
  ).trim();
  memberJoinedModalLinkPath.value = String(payload?.toastLinkPath || payload?.linkPath || '').trim();
  memberJoinedModalOpen.value = true;
};


function normalizeCreatorNewsfeedAvatarUrl(rawUrl, instanceId = null) {
  const raw = String(rawUrl || '').trim();
  if (!raw) return '';

  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;
  const params = new URLSearchParams();
  const iid = Number(instanceId);
  if (Number.isFinite(iid) && iid > 0) {
    params.set('featureInstanceId', String(iid));
  }
  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}${qs ? `?${qs}` : ''}`;
}

function normalizeCreatorVideoChannelAvatarUrl(payload = {}) {
  const fileId = String(payload?.channelAvatarMediaFileId || '').trim();
  const instanceId = Number(payload?.featureInstanceId || payload?.instanceId || 0);
  if (fileId && Number.isFinite(instanceId) && instanceId > 0) {
    const params = new URLSearchParams({ featureInstanceId: String(Math.trunc(instanceId)) });
    return `${apiProxyUrl}/api/creator-video-channel/image/${encodeURIComponent(fileId)}?${params.toString()}`;
  }
  return String(
    payload?.creatorAvatarUrl ||
      payload?.channelAvatarUrl ||
      payload?.authorAvatarUrl ||
      payload?.avatarUrl ||
      ''
  ).trim();
}

function normalizeCreatorFlixTheatreAvatarUrl(payload = {}) {
  const fileId = String(
    payload?.creatorLogoMediaFileId ||
    payload?.theatreLogoMediaFileId ||
    payload?.theatreAvatarMediaFileId ||
    payload?.channelAvatarMediaFileId ||
    ''
  ).trim();
  const instanceId = Number(payload?.featureInstanceId || payload?.instanceId || 0);
  if (fileId && Number.isFinite(instanceId) && instanceId > 0) {
    const params = new URLSearchParams({ featureInstanceId: String(Math.trunc(instanceId)) });
    return `${apiProxyUrl}/api/creator-flix-theatre/image/${encodeURIComponent(fileId)}?${params.toString()}`;
  }
  const direct = String(
    payload?.creatorLogoUrl ||
      payload?.theatreLogoUrl ||
      payload?.creatorAvatarUrl ||
      payload?.theatreAvatarUrl ||
      payload?.channelAvatarUrl ||
      payload?.authorAvatarUrl ||
      payload?.avatarUrl ||
      ''
  ).trim();
  return direct || `${apiProxyUrl}/api/main-app-logo`;
}

function buildVideoChannelToastDedupeId(payload = {}) {
  const videoId = Number(payload?.videoId || 0);
  const publishedAt = String(payload?.publishedAt || '').trim();
  const recipientType = String(payload?.recipientUserType || '').toLowerCase().trim();
  if (!videoId) return '';
  return `video-published:${videoId}:${publishedAt}:${recipientType}`;
}

function buildFlixTheatreToastDedupeId(payload = {}) {
  const videoId = Number(payload?.videoId || 0);
  const publishedAt = String(payload?.publishedAt || '').trim();
  const recipientType = String(payload?.recipientUserType || '').toLowerCase().trim();
  if (!videoId) return '';
  return `flix-video-published:${videoId}:${publishedAt}:${recipientType}`;
}

function buildStorefrontOrderToastDedupeId(payload = {}) {
  const orderId = Number(payload?.orderId || 0);
  const confirmationSlug = String(payload?.confirmationSlug || '').trim();
  const ts = String(payload?.ts || '').trim();
  if (orderId > 0) return `storefront-order:${orderId}:${confirmationSlug}:${ts}`;
  if (confirmationSlug) return `storefront-order:${confirmationSlug}:${ts}`;
  return '';
}

function buildCryptoReceiveToastDedupeId(payload = {}) {
  const notificationSlug = String(payload?.notificationSlug || '').trim();
  if (notificationSlug) return `crypto:${notificationSlug}`;
  const operationId = Number(payload?.operationId || 0);
  const paymentHash = String(payload?.paymentHash || '').trim();
  const txid = String(payload?.txid || '').trim();
  const ts = String(payload?.ts || '').trim();
  if (operationId > 0) return `crypto:op:${operationId}:${ts}`;
  if (paymentHash) return `crypto:ln:${paymentHash}:${ts}`;
  if (txid) return `crypto:onchain:${txid}:${ts}`;
  return '';
}

function formatCryptoReceiveAmount(payload = {}) {
  const rawBtc = String(payload?.amountBtc || '').trim();
  if (rawBtc) return rawBtc;
  const sats = Number(payload?.amountSats || 0);
  if (!Number.isFinite(sats) || sats <= 0) return '0.0';
  const btc = (sats / 100000000).toFixed(8).replace(/\.?0+$/, '');
  if (!btc) return '0.0';
  return btc.includes('.') ? btc : `${btc}.0`;
}


async function resolveNewsfeedInstanceName(instanceId) {
  const iid = Number(instanceId);
  if (!Number.isFinite(iid) || iid <= 0) return 'this newsfeed';

  const key = String(iid);
  if (newsfeedInstanceNameCache.has(key)) {
    return newsfeedInstanceNameCache.get(key) || 'this newsfeed';
  }

  try {
    const res = await fetch(`${apiProxyUrl}/api/feature-instances`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      const rawList =
        data?.instances ||
        data?.featureInstances ||
        data?.items ||
        (Array.isArray(data) ? data : []);

      if (Array.isArray(rawList)) {
        const match = rawList.find((inst) => {
          const id = Number(inst?.instance_id ?? inst?.feature_instance_id ?? inst?.id);
          return Number.isFinite(id) && id === iid;
        });

        const customName = String(match?.custom_name || match?.customName || '').trim();
        if (customName) {
          newsfeedInstanceNameCache.set(key, customName);
          return customName;
        }
      }
    }
  } catch {
    // fall through to name endpoint/header resolver
  }

  try {
    const res = await fetch(`${apiProxyUrl}/api/feature-instances/${encodeURIComponent(key)}/name`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      const fromNameEndpoint = String(
        data?.custom_name || data?.customName || data?.name || data?.instanceName || ''
      ).trim();
      if (fromNameEndpoint) {
        newsfeedInstanceNameCache.set(key, fromNameEndpoint);
        return fromNameEndpoint;
      }
    }
  } catch {
    // fall through to legacy header resolver
  }

  try {
    const params = new URLSearchParams({ featureInstanceId: key });
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/instance-header?${params.toString()}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) throw new Error(`instance-header failed (${res.status})`);

    const data = await res.json().catch(() => ({}));
    const name = String(data?.groupName || '').trim() || `Newsfeed #${key}`;
    newsfeedInstanceNameCache.set(key, name);
    return name;
  } catch {
    return `Newsfeed #${key}`;
  }
}
async function buildCreatorNewsfeedToastDetails(payload) {
  const slug = String(payload?.slug || '').trim();
  const status = String(payload?.status || '').toLowerCase();
  const defaultTitle = payload?.authorUserType === 'member' ? 'Member' : 'Creator';
  const payloadAuthorName = String(payload?.authorName || '').trim();
  const payloadAuthorAvatar = normalizeCreatorNewsfeedAvatarUrl(
    payload?.authorAvatar || '',
    payload?.instanceId
  );
  const payloadToastTitle = String(payload?.toastTitle || '').trim();
  const payloadToastBody = String(payload?.toastBody || '').trim();
  const payloadToastLinkPath = String(payload?.toastLinkPath || '').trim();
  const payloadToastLinkLabel = String(payload?.toastLinkLabel || '').trim() || 'View post';
  const isSelfPost = Boolean(payload?.selfPost);
  const instanceName = await resolveNewsfeedInstanceName(payload?.instanceId);

  const pendingLinkPath = '/creator/newsfeed?tab=Pending';

  if (isSelfPost) {
    return {
      title: payloadToastTitle || 'You',
      body: payloadToastBody || 'Your post was just added to the Discussion.',
      avatarUrl: payloadAuthorAvatar || '',
      postBadge: true,
      linkPath: payloadToastLinkPath || (
        status === 'pending_approval'
          ? pendingLinkPath
          : slug
            ? `/creator/newsfeed/post/${encodeURIComponent(slug)}`
            : ''
      ),
      linkLabel: payloadToastLinkLabel,
    };
  }

  if (!slug) {
    return {
      title: payloadToastTitle || payloadAuthorName || defaultTitle,
      body: payloadToastBody || (
        status === 'pending_approval'
          ? `has a Pending Post waiting your approval in ${instanceName}.`
          : `just posted in ${instanceName}.`
      ),
      avatarUrl: payloadAuthorAvatar || '',
      postBadge: true,
      linkPath: payloadToastLinkPath || (status === 'pending_approval' ? pendingLinkPath : ''),
      linkLabel: payloadToastLinkLabel,
    };
  }

  const linkPath = status === 'pending_approval'
    ? pendingLinkPath
    : `/creator/newsfeed/post/${encodeURIComponent(slug)}`;

  try {
    const res = await fetch(
      `${apiProxyUrl}/api/creator-newsfeed/posts/by-slug/${encodeURIComponent(slug)}`,
      {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) throw new Error(`post-by-slug failed (${res.status})`);

    const data = await res.json().catch(() => ({}));
    const post = data?.post || {};
    const fetchedDisplayName =
      String(post.postedAsName || post.authorName || post.authorAlias || defaultTitle).trim() ||
      defaultTitle;
    const displayName = payloadAuthorName || fetchedDisplayName;
    const avatarUrl =
      payloadAuthorAvatar ||
      normalizeCreatorNewsfeedAvatarUrl(
        post.postedAsAvatarUrl || post.authorAvatar || '',
        post.featureInstanceId || post.instanceId || payload?.instanceId
      );

    return {
      title: payloadToastTitle || displayName,
      body: payloadToastBody || (
        status === 'pending_approval'
          ? `has a Pending Post waiting your approval in ${instanceName}.`
          : `just posted in ${instanceName}.`
      ),
      avatarUrl,
      postBadge: true,
      linkPath: payloadToastLinkPath || linkPath,
      linkLabel: payloadToastLinkLabel,
    };
  } catch {
    return {
      title: payloadToastTitle || payloadAuthorName || defaultTitle,
      body: payloadToastBody || (
        status === 'pending_approval'
          ? `has a Pending Post waiting your approval in ${instanceName}.`
          : `just posted in ${instanceName}.`
      ),
      avatarUrl: payloadAuthorAvatar || '',
      postBadge: true,
      linkPath: payloadToastLinkPath || linkPath,
      linkLabel: payloadToastLinkLabel,
    };
  }
}

async function buildCreatorNewsfeedCommentToastDetails(payload) {
  const slug = String(payload?.slug || '').trim();
  const payloadAuthorName = String(payload?.authorName || '').trim();
  const payloadAuthorAvatar = normalizeCreatorNewsfeedAvatarUrl(
    payload?.authorAvatar || '',
    payload?.instanceId
  );
  const isSelfComment = Boolean(payload?.selfComment);
  const isYourPostTarget = Boolean(payload?.yourPostTarget);
  const instanceName = await resolveNewsfeedInstanceName(payload?.instanceId);

  if (isSelfComment) {
    return {
      title: 'You',
      body: 'Your Comment was added to the post.',
      avatarUrl: payloadAuthorAvatar || '',
      reactionType: 'comment',
      linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
      linkLabel: 'View post',
    };
  }

  return {
    title: payloadAuthorName || 'Someone',
    body: isYourPostTarget
      ? `just commented on YOUR post in ${instanceName}.`
      : `just commented on a post in ${instanceName}.`,
    avatarUrl: payloadAuthorAvatar || '',
    reactionType: 'comment',
    linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
    linkLabel: 'View post',
  };
}

async function buildCreatorNewsfeedReplyToastDetails(payload) {
  const slug = String(payload?.slug || '').trim();
  const payloadAuthorName = String(payload?.authorName || '').trim();
  const payloadAuthorAvatar = normalizeCreatorNewsfeedAvatarUrl(
    payload?.authorAvatar || '',
    payload?.instanceId
  );
  const isSelfReply = Boolean(payload?.selfReply);
  const isYourCommentTarget = Boolean(payload?.yourCommentTarget);
  const instanceName = await resolveNewsfeedInstanceName(payload?.instanceId);

  if (isSelfReply) {
    return {
      title: 'You',
      body: 'Your Reply was added to the comment thread.',
      avatarUrl: payloadAuthorAvatar || '',
      reactionType: 'comment',
      linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
      linkLabel: 'View post',
    };
  }

  return {
    title: payloadAuthorName || 'Someone',
    body: isYourCommentTarget
      ? `just replied to YOUR comment in ${instanceName}.`
      : `just replied to a comment in ${instanceName}.`,
    avatarUrl: payloadAuthorAvatar || '',
    reactionType: 'comment',
    linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
    linkLabel: 'View post',
  };
}

async function buildCreatorNewsfeedPostReactionToastDetails(payload) {
  const slug = String(payload?.slug || '').trim();
  const payloadAuthorName = String(payload?.authorName || '').trim();
  const payloadAuthorAvatar = normalizeCreatorNewsfeedAvatarUrl(
    payload?.authorAvatar || '',
    payload?.instanceId
  );
  const reactionType = String(payload?.reactionType || 'like').toLowerCase().trim();
  const reactionVerb = reactionType === 'like' ? 'liked' : reactionType === 'love' ? 'loved' : 'reacted to';
  const isSelfReaction = Boolean(payload?.selfReaction);
  const isYourPostTarget = Boolean(payload?.yourPostTarget);
  const instanceName = await resolveNewsfeedInstanceName(payload?.instanceId);

  if (isSelfReaction) {
    return {
      title: 'You',
      body: 'Your Reaction was added to the post.',
      avatarUrl: payloadAuthorAvatar || '',
      reactionType,
      linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
      linkLabel: 'View post',
    };
  }

  return {
    title: payloadAuthorName || 'Someone',
    body: isYourPostTarget
      ? `${reactionVerb} YOUR post in ${instanceName}.`
      : `${reactionVerb} a post in ${instanceName}.`,
    avatarUrl: payloadAuthorAvatar || '',
    reactionType,
    linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
    linkLabel: 'View post',
  };
}

async function buildCreatorNewsfeedCommentReactionToastDetails(payload) {
  const slug = String(payload?.slug || '').trim();
  const payloadAuthorName = String(payload?.authorName || '').trim();
  const payloadAuthorAvatar = normalizeCreatorNewsfeedAvatarUrl(
    payload?.authorAvatar || '',
    payload?.instanceId
  );
  const reactionType = String(payload?.reactionType || 'like').toLowerCase().trim();
  const reactionVerb = reactionType === 'like' ? 'liked' : reactionType === 'love' ? 'loved' : 'reacted to';
  const isSelfReaction = Boolean(payload?.selfReaction);
  const isYourCommentTarget = Boolean(payload?.yourCommentTarget);
  const instanceName = await resolveNewsfeedInstanceName(payload?.instanceId);

  if (isSelfReaction) {
    return {
      title: 'You',
      body: 'Your Reaction was added to the comment thread.',
      avatarUrl: payloadAuthorAvatar || '',
      reactionType,
      linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
      linkLabel: 'View post',
    };
  }

  return {
    title: payloadAuthorName || 'Someone',
    body: isYourCommentTarget
      ? `${reactionVerb} YOUR comment/reply in ${instanceName}.`
      : `${reactionVerb} a comment/reply in ${instanceName}.`,
    avatarUrl: payloadAuthorAvatar || '',
    reactionType,
    linkPath: slug ? `/creator/newsfeed/post/${encodeURIComponent(slug)}` : '',
    linkLabel: 'View post',
  };
}

function updateCreatorBrowserTitle() {
  if (typeof document === 'undefined') return;
  const raw = String(document.title || 'Creator').replace(/^\(\d+\+?\)\s+/, '');
  document.title = creatorUnreadCount.value > 0
    ? `(${creatorUnreadCountLabel.value}) ${raw}`
    : raw;
}

async function fetchCreatorUnreadCount() {
  if (!canAccessNotifications.value) {
    creatorUnreadCount.value = 0;
    creatorUnreadCountLabel.value = '0';
    return;
  }

  try {
    const res = await fetch(`${apiProxyUrl}/api/notifications?limit=1&offset=0`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!res.ok) return;

    const data = await res.json().catch(() => ({}));
    const next = Number(data?.unreadCount || 0);
    if (!Number.isFinite(next) || next < 0) return;

    creatorUnreadCount.value = next;
    creatorUnreadCountLabel.value = next > 99 ? '99+' : String(next);

    updateCreatorBrowserTitle();
  } catch (err) {
    console.warn('[CreatorLayout] fetchCreatorUnreadCount error:', err);
  }
}

const playIncomingMessageSound = () => {
  if (!incomingMessageAudio) return;

  try {
    incomingMessageAudio.currentTime = 0;
    incomingMessageAudio.play().catch((err) => {
      console.warn('[CreatorLayout] Unable to play incoming message sound:', err);
    });
  } catch (err) {
    console.warn('[CreatorLayout] Error triggering sound:', err);
  }
};

const playStorefrontOrderConfirmedSound = () => {
  if (!storefrontOrderConfirmedAudio) return;

  try {
    storefrontOrderConfirmedAudio.currentTime = 0;
    storefrontOrderConfirmedAudio.play().catch((err) => {
      console.warn('[CreatorLayout] Unable to play storefront sell sound:', err);
    });
  } catch (err) {
    console.warn('[CreatorLayout] Error triggering storefront sell sound:', err);
  }
};

const performCreatorSessionExit = async ({ reason = 'logout', notifyBackend = true } = {}) => {
  if (creatorExitInProgress.value) return;
  creatorExitInProgress.value = true;
  const logoutTransitionStartedAt = reason === 'logout' ? loginTransition?.startLogout() : null;

  if (websocketStore.disconnectForLogout) {
    websocketStore.disconnectForLogout();
  }
  websocketStore.stopHeartbeat && websocketStore.stopHeartbeat();

  if (creatorUnreadPollTimer) {
    clearInterval(creatorUnreadPollTimer);
    creatorUnreadPollTimer = null;
  }

  try {
    if (notifyBackend) {
      const logoutUrl = apiProxyUrl ? `${apiProxyUrl}/api/auth/logout` : '/api/auth/logout';
      await fetch(logoutUrl, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
  } catch (error) {
    console.warn('[CreatorLayout] Session exit logout request failed:', error);
  } finally {
    if (typeof window !== 'undefined') {
      if (logoutTransitionStartedAt) {
        await loginTransition.waitForLogout(logoutTransitionStartedAt);
        try {
          window.sessionStorage.setItem('platforma_creator_logout_transition', '1');
        } catch (_error) {
          // The session still ends normally if browser storage is unavailable.
        }
      }
      const loginUrl = new URL('/', window.location.origin);
      if (reason === 'session-expired') {
        loginUrl.searchParams.set('reason', 'session-expired');
      }
      window.location.replace(loginUrl.toString());
    }
  }
};

const handleLogout = async () => {
  await performCreatorSessionExit({ reason: 'logout', notifyBackend: true });
};


const sidebarOpen = ref(false)
const isOnlinePlatformMenuOpen = ref(true)
const isEngageMenuOpen = ref(true)

const creatorPageTransitionKey = (viewRoute) => {
  const topLevelCreatorRecord = Array.isArray(viewRoute?.matched) ? viewRoute.matched[1] : null
  return String(topLevelCreatorRecord?.path || viewRoute?.name || viewRoute?.path || 'creator-page')
}

const fullNavigation = Object.freeze([
  { name: 'Dashboard', to: '/creator/dashboard', icon: HomeIcon, permission: 'main.dashboard' },
  { name: 'Members', to: '/creator/users', icon: UsersIcon, permission: 'main.members' },
  { name: 'Media Content', to: '/creator/content-drive', icon: PhotoIcon, permission: 'main.mediaContent' },
  { name: 'Analytics', to: '/creator/analytics', icon: ChartBarIcon, permission: 'main.analytics' },
  { name: 'Cart', to: '/creator/cart/products', icon: ShoppingCartIcon, permission: 'main.payments' },
  { name: 'Payments', to: '/creator/payments', icon: BanknotesIcon, permission: 'main.payments' },
  { name: 'Advertising', to: '/creator/advertising', icon: CurrencyDollarIcon, permission: 'main.payments' },
]);

const fullEngageNavigation = Object.freeze([
  { name: 'Chat', to: '/creator/chat', icon: ChatBubbleBottomCenterTextIcon, permission: 'engage.chat' },
  { name: 'Stream', to: '/creator/stream', icon: VideoCameraIcon, permission: 'engage.stream' },
  { name: 'Meet', to: '/creator/meet', icon: PhoneArrowUpRightIcon, permission: 'engage.stream' },
  { name: 'Post', to: '/creator/newsfeed', icon: PaperAirplaneIcon, permission: 'engage.post' },
  { name: 'Broadcast', to: '/creator/broadcast', icon: MegaphoneIcon, permission: 'engage.broadcast' },
]);

const fullOnlinePlatformNavigation = Object.freeze([
  { name: 'Theme Editor', to: '/creator/theme-editor', icon: WrenchScrewdriverIcon, permission: 'platform.themeEditor', isThemeEditor: true },
  { name: 'Page Editor', to: '/creator/page-editor', icon: DocumentTextIcon, permission: 'platform.pageEditor' },
  { name: 'Membership Settings', to: '/creator/app-settings', icon: Cog6ToothIcon, permission: 'platform.memberSettings' },
]);

const featureMetadataBySlug = computed(() => {
  const map = new Map();
  const rows = Array.isArray(featureStore?.state?.featureInstances) ? featureStore.state.featureInstances : [];
  for (const row of rows) {
    const slug = String(row?.slug || '').trim().toLowerCase();
    if (!slug) continue;
    const customName = String(row?.custom_name || row?.customName || '').trim();
    const featureName = String(row?.feature_name || row?.featureName || row?.displayName || '').trim();
    map.set(slug, {
      displayName: customName || featureName || slug,
      featureName,
      iconName: String(row?.icon || '').trim(),
    });
  }
  return map;
});

const productNavigationIconByName = Object.freeze({
  AcademicCapIcon,
  BookOpenIcon,
  BuildingStorefrontIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  FilmIcon,
  GlobeAltIcon,
  MicrophoneIcon,
  NewspaperIcon,
  PhoneArrowUpRightIcon,
  QueueListIcon,
  ShoppingCartIcon,
  VideoCameraIcon,
});

const resolveProductNavigationIcon = (iconName, featureName) => {
  const directMatch = productNavigationIconByName[String(iconName || '').trim()];
  if (directMatch) return directMatch;

  const normalizedName = String(featureName || '').trim().toLowerCase();
  if (normalizedName.includes('course')) return AcademicCapIcon;
  if (normalizedName.includes('video')) return VideoCameraIcon;
  if (normalizedName.includes('flix')) return FilmIcon;
  if (normalizedName.includes('journal')) return BookOpenIcon;
  if (normalizedName.includes('webinar')) return GlobeAltIcon;
  if (normalizedName.includes('store')) return BuildingStorefrontIcon;
  if (normalizedName.includes('newsfeed')) return NewspaperIcon;
  if (normalizedName.includes('quiz')) return QueueListIcon;
  if (normalizedName.includes('meet')) return PhoneArrowUpRightIcon;
  if (normalizedName.includes('podcast')) return MicrophoneIcon;
  if (normalizedName.includes('article')) return DocumentTextIcon;
  if (normalizedName.includes('chat')) return ChatBubbleBottomCenterTextIcon;
  return Squares2X2Icon;
};

const staffStockroomNavigation = computed(() => {
  if (!isStaffAccount.value || hasShellRouteAccess('/creator/theme-editor')) return [];
  const permissions = (staffSession.value?.permissions && typeof staffSession.value.permissions === 'object')
    ? staffSession.value.permissions
    : {};
  const coreFeatures = (permissions.coreFeatures && typeof permissions.coreFeatures === 'object')
    ? permissions.coreFeatures
    : {};
  const slugs = new Set();

  for (const [rawKey, rawValue] of Object.entries(coreFeatures)) {
    if (!rawValue) continue;
    const key = String(rawKey || '').trim().toLowerCase();
    if (!key) continue;
    if (key.includes('::stockroom.')) {
      slugs.add(String(key.split('::stockroom.')[0] || '').trim());
      continue;
    }
    if (key.startsWith('platform.')) continue;
    slugs.add(key);
  }

  const items = [];
  for (const slug of slugs) {
    if (!slug) continue;
    const allowedTabs = getAllowedStockroomTabsForFeature(staffSession.value, slug);
    if (!Array.isArray(allowedTabs) || !allowedTabs.length) continue;
    const preferredTab = allowedTabs.includes('orders') ? 'orders' : String(allowedTabs[0] || 'orders').trim().toLowerCase();
    const metadata = featureMetadataBySlug.value.get(slug) || {};
    const prettyName = metadata.displayName || slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
    items.push({
      name: prettyName,
      to: `/creator/stockroom/${encodeURIComponent(slug)}/${preferredTab}`,
      icon: resolveProductNavigationIcon(metadata.iconName, metadata.featureName || prettyName),
      permission: null,
      isThemeEditor: false,
    });
  }

  items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  return items;
});

const navigation = computed(() => fullNavigation.filter((item) => hasShellRouteAccess(item.to)));
const engageNavigation = computed(() => fullEngageNavigation.filter((item) => hasShellRouteAccess(item.to)));
const onlinePlatformNavigation = computed(() => {
  const baseItems = fullOnlinePlatformNavigation.filter((item) => hasShellRouteAccess(item.to));
  const stockroomItems = staffStockroomNavigation.value.filter((item) => hasShellRouteAccess(item.to));
  return [...baseItems, ...stockroomItems];
});
const canAccessAccountSettings = computed(() =>
  hasShellRouteAccess('/creator/account/general')
  || hasShellRouteAccess('/creator/account/security')
);
const accountSettingsPath = computed(() =>
  hasShellRouteAccess('/creator/account/general')
    ? '/creator/account/general'
    : '/creator/account/security'
);
const canAccessSupport = computed(() => hasShellRouteAccess('/creator/support'));
const canAccessNotifications = computed(() =>
  Boolean(staffSession.value)
  && !staffSession.value?.isStaff
  && hasShellRouteAccess('/creator/notifications')
);



const currentRoute = ref(route.path)

const setActiveRoute = (path) => {
  currentRoute.value = path;
}

const toggleOnlinePlatformMenu = () => {
  isOnlinePlatformMenuOpen.value = !isOnlinePlatformMenuOpen.value
}
const toggleEngageMenu = () => {
  isEngageMenuOpen.value = !isEngageMenuOpen.value
}


const creatorFullName = ref('');
const isStaffAccount = ref(false);
const accountRoleLabel = ref('Admin');
const creatorOwnerLabel = ref('');
const staffSession = ref(null);


const defaultAvatarSrc = defaultAvatarAsset;
const profilePicSrc = ref('');
const isLoading = ref(false);

const normalizeProfilePicSrc = (value) => {
  const source = String(value || '').trim();
  if (!source || source.includes('src/assets/media/images/default-avatar.png') || source.startsWith('@/')) {
    return defaultAvatarSrc;
  }
  return source;
};

const handleProfileImageError = (event) => {
  const image = event?.currentTarget;
  if (!image || image.dataset.fallbackApplied === 'true') return;
  image.dataset.fallbackApplied = 'true';
  profilePicSrc.value = defaultAvatarSrc;
  image.src = defaultAvatarSrc;
};

const readIdentityName = (person = {}) => {
  const firstName = String(person?.firstName || '').trim();
  const lastName = String(person?.lastName || '').trim();
  const email = String(person?.email || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || email || '';
};

const hasShellRouteAccess = (pathValue) => {
  return canAccessCreatorPath(staffSession.value, pathValue);
};

const loadShellSession = async () => {
  isLoading.value = true;
  try {
    const session = await fetchCreatorStaffSession({ force: true });
    staffSession.value = session || null;

    const staffName = readIdentityName(session?.staff || {});
    const creatorName = readIdentityName(session?.creator || {});

    isStaffAccount.value = Boolean(session?.isStaff);
    accountRoleLabel.value = isStaffAccount.value ? 'Staff' : 'Admin';
    creatorOwnerLabel.value = isStaffAccount.value ? creatorName : '';

    creatorFullName.value = isStaffAccount.value ? (staffName || 'Staff User') : (creatorName || 'Creator Admin');

    const preferredProfile = isStaffAccount.value
      ? String(session?.staff?.profilePic || '').trim()
      : String(session?.creator?.profilePic || '').trim();
    profilePicSrc.value = normalizeProfilePicSrc(preferredProfile);

    if (isStaffAccount.value) {
      creatorUnreadCount.value = 0;
      creatorUnreadCountLabel.value = '0';
      updateCreatorBrowserTitle();
    } else {
      void fetchCreatorUnreadCount();
    }

    if (isStaffAccount.value && typeof featureStore.fetchFeatureInstances === 'function') {
      await featureStore.fetchFeatureInstances();
    }
  } catch (error) {
    console.error('Error loading creator/staff shell session:', error);
    profilePicSrc.value = defaultAvatarSrc;
    creatorFullName.value = 'Creator Admin';
    isStaffAccount.value = false;
    accountRoleLabel.value = 'Admin';
    creatorOwnerLabel.value = '';
  } finally {
    isLoading.value = false;
  }
};

// 🔌 Global WS listener for any incoming chat message
onMounted(() => {
  creatorAuthExpiredListener = (event) => {
    const reason = String(event?.detail?.reason || 'session-expired').trim() || 'session-expired';
    void performCreatorSessionExit({ reason, notifyBackend: true });
  };
  window.addEventListener(CREATOR_AUTH_EXPIRED_EVENT, creatorAuthExpiredListener);
  // ensure WS is connected
  websocketStore.connect();
  void fetchCreatorUnreadCount();
  creatorUnreadPollTimer = setInterval(() => {
    void fetchCreatorUnreadCount();
  }, 30000);

  // init audio once
  initIncomingMessageAudio();
  initStorefrontOrderConfirmedAudio();

  // subscribe to all chat:newMessage events
  unsubscribeChatNewMessage = websocketStore.subscribe(
    'chat:newMessage',
    (payload) => {
      if (!payload) return;

      // We only care about messages **from the member** (incoming)
      if (payload.sender_type && payload.sender_type !== 'member') {
        return;
      }

      // At this level we don't care which member / feature instance;
      // the chat component itself already filters for display.
      playIncomingMessageSound();
    }
  );

 // 🔥 presence: bulk snapshot from backend
  unsubscribePresenceSnapshot = websocketStore.subscribe(
    'presence:bulkMemberSnapshot',
    (payload) => {
      if (!payload || !Array.isArray(payload.results)) return;
      presenceStore.applyMemberSnapshot(
        payload.results,
        payload.userType || 'member'
      );
    }
  );

  // 🔥 presence: single-member update
  unsubscribePresenceUpdate = websocketStore.subscribe(
    'presence:memberUpdate',
    (payload) => {
      if (!payload) return;
      presenceStore.applyMemberUpdate(payload);
    }
  );
  unsubscribeNewsfeedPostCreated = websocketStore.subscribe(
    'newsfeed:postToast',
    (payload) => {
      if (!payload) return;

      const postToastKey = [
        'post',
        payload.postId != null ? String(payload.postId) : '',
        String(payload.status || '').toLowerCase(),
        String(payload.moderationAction || '').toLowerCase(),
        payload.selfPost ? 'self' : 'other',
      ].join(':');
      if (seenNewsfeedToastPostIds.has(postToastKey)) return;
      seenNewsfeedToastPostIds.add(postToastKey);

      // Avoid duplicate toast on newsfeed routes where page-level handlers already notify.
      // Exception: pending-approval submissions must still toast for creator moderation awareness.
      const onCreatorNewsfeedRoute = String(route.path || '').startsWith('/creator/newsfeed');
      const isPendingApprovalToast = String(payload?.status || '').toLowerCase() === 'pending_approval';
      if (!payload?.selfPost && onCreatorNewsfeedRoute && !isPendingApprovalToast) return;

      if (typeof notify !== 'function') return;

      void buildCreatorNewsfeedToastDetails(payload).then((toast) => {
        notify({
          title: toast.title,
          body: toast.body,
          avatarUrl: toast.avatarUrl,
          postBadge: toast.postBadge,
          linkPath: toast.linkPath,
          linkLabel: toast.linkLabel,
          variant: 'success',
          duration: payload?.selfPost ? 10000 : 45000,
        });
        void fetchCreatorUnreadCount();
      });
    }
  );
  unsubscribeNewsfeedCommentToast = websocketStore.subscribe(
    'newsfeed:commentToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = payload.commentId != null ? `comment:${String(payload.commentId)}:${payload.selfComment ? 'self' : payload.yourPostTarget ? 'your' : 'all'}` : '';
      if (dedupeId && seenNewsfeedToastPostIds.has(dedupeId)) return;
      if (dedupeId) seenNewsfeedToastPostIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      void buildCreatorNewsfeedCommentToastDetails(payload).then((toast) => {
        notify({
          title: toast.title,
          body: toast.body,
          avatarUrl: toast.avatarUrl,
          reactionType: toast.reactionType,
          linkPath: toast.linkPath,
          linkLabel: toast.linkLabel,
          variant: 'success',
          duration: payload?.selfComment ? 10000 : 45000,
        });
        void fetchCreatorUnreadCount();
      });
    }
  );
  unsubscribeNewsfeedReplyToast = websocketStore.subscribe(
    'newsfeed:replyToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = payload.commentId != null ? `reply:${String(payload.commentId)}:${payload.selfReply ? 'self' : payload.yourCommentTarget ? 'your' : 'all'}` : '';
      if (dedupeId && seenNewsfeedToastPostIds.has(dedupeId)) return;
      if (dedupeId) seenNewsfeedToastPostIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      void buildCreatorNewsfeedReplyToastDetails(payload).then((toast) => {
        notify({
          title: toast.title,
          body: toast.body,
          avatarUrl: toast.avatarUrl,
          reactionType: toast.reactionType,
          linkPath: toast.linkPath,
          linkLabel: toast.linkLabel,
          variant: 'success',
          duration: payload?.selfReply ? 10000 : 45000,
        });
        void fetchCreatorUnreadCount();
      });
    }
  );
  unsubscribeNewsfeedPostReactionToast = websocketStore.subscribe(
    'newsfeed:postReactionToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = payload.postId != null ? `post-reaction:${String(payload.postId)}:${payload.selfReaction ? 'self' : payload.yourPostTarget ? 'your' : 'all'}` : '';
      if (dedupeId && seenNewsfeedToastPostIds.has(dedupeId)) return;
      if (dedupeId) seenNewsfeedToastPostIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      void buildCreatorNewsfeedPostReactionToastDetails(payload).then((toast) => {
        notify({
          title: toast.title,
          body: toast.body,
          avatarUrl: toast.avatarUrl,
          reactionType: toast.reactionType,
          linkPath: toast.linkPath,
          linkLabel: toast.linkLabel,
          variant: 'success',
          duration: payload?.selfReaction ? 10000 : 45000,
        });
        void fetchCreatorUnreadCount();
      });
    }
  );
  unsubscribeNewsfeedCommentReactionToast = websocketStore.subscribe(
    'newsfeed:commentReactionToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = payload.commentId != null ? `comment-reaction:${String(payload.commentId)}:${payload.selfReaction ? 'self' : payload.yourCommentTarget ? 'your' : 'all'}` : '';
      if (dedupeId && seenNewsfeedToastPostIds.has(dedupeId)) return;
      if (dedupeId) seenNewsfeedToastPostIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      void buildCreatorNewsfeedCommentReactionToastDetails(payload).then((toast) => {
        notify({
          title: toast.title,
          body: toast.body,
          avatarUrl: toast.avatarUrl,
          reactionType: toast.reactionType,
          linkPath: toast.linkPath,
          linkLabel: toast.linkLabel,
          variant: 'success',
          duration: payload?.selfReaction ? 10000 : 45000,
        });
        void fetchCreatorUnreadCount();
      });
    }
  );
  unsubscribeMemberLifecycle = websocketStore.subscribe('member:lifecycle', (payload) => {
    if (!payload || typeof payload !== 'object') return;
    emitMemberLifecycleUpdate(payload);
  });
  unsubscribeMemberJoinedToast = websocketStore.subscribe('member:joinedToast', (payload) => {
    if (!payload || typeof payload !== 'object') return;
    const dedupeId = toMemberLifecycleEventKey(payload);
    if (dedupeId && seenMemberJoinedToastIds.has(dedupeId)) return;
    if (dedupeId) seenMemberJoinedToastIds.add(dedupeId);

    emitMemberLifecycleUpdate({
      ...payload,
      event: String(payload?.event || 'member_joined').trim() || 'member_joined',
    });

    if (typeof notify === 'function') {
      const memberDisplayName = String(payload?.memberName || payload?.memberEmail || 'A new member').trim() || 'A new member';
      notify({
        title: String(payload?.toastTitle || 'Congrats!').trim() || 'Congrats!',
        body: String(payload?.toastBody || `${memberDisplayName} just Joined!`).trim(),
        avatarUrl: String(payload?.memberAvatarUrl || '').trim() || defaultAvatarSrc,
        postBadge: true,
        linkPath: String(payload?.toastLinkPath || payload?.linkPath || '').trim(),
        linkLabel: String(payload?.toastLinkLabel || 'See New Member').trim() || 'See New Member',
        variant: 'success',
        duration: 15000,
      });
    }

    openMemberJoinedModal(payload);
    void fetchCreatorUnreadCount();
  });
  unsubscribeMemberSubscriptionToast = websocketStore.subscribe('member:subscriptionToast', (payload) => {
    if (!payload || typeof payload !== 'object') return;
    const dedupeId = toMemberLifecycleEventKey(payload);
    if (dedupeId && seenMemberSubscriptionToastIds.has(dedupeId)) return;
    if (dedupeId) seenMemberSubscriptionToastIds.add(dedupeId);

    emitMemberLifecycleUpdate({
      ...payload,
      event: String(payload?.event || 'member_subscription_update').trim() || 'member_subscription_update',
    });

    if (typeof notify === 'function') {
      const memberDisplayName = String(payload?.memberName || payload?.memberEmail || 'Member').trim() || 'Member';
      const eventType = String(payload?.event || '').toLowerCase().trim();
      const toastVariant = String(payload?.toastVariant || '').toLowerCase().trim();
      const isPaymentFailedEvent =
        eventType === 'member_subscription_payment_failed' ||
        eventType === 'member.subscription_payment_failed' ||
        eventType === 'subscription_payment_failed';
      notify({
        title: String(payload?.toastTitle || memberDisplayName).trim() || memberDisplayName,
        body: String(payload?.toastBody || '').trim(),
        avatarUrl: String(payload?.memberAvatarUrl || '').trim() || defaultAvatarSrc,
        postBadge: true,
        linkPath: String(payload?.toastLinkPath || payload?.linkPath || '').trim(),
        linkLabel: String(payload?.toastLinkLabel || 'Subscription Details').trim() || 'Subscription Details',
        variant: toastVariant || (isPaymentFailedEvent ? 'error' : 'success'),
        duration: 15000,
      });
    }

    void fetchCreatorUnreadCount();
  });
  unsubscribeNotificationsRefresh = websocketStore.subscribe('notifications:refresh', () => {
    void fetchCreatorUnreadCount();
  });
  unsubscribeMeetNotificationToast = websocketStore.subscribe('meet:notificationToast', (payload) => {
    if (!payload || typeof payload !== 'object') return;
    const dedupeId = String(
      payload?.notificationSlug ||
      `${payload?.eventType || 'meet'}:${payload?.appointmentId || ''}:${payload?.ts || ''}`
    ).trim();
    if (dedupeId && seenMeetNotificationToastIds.has(dedupeId)) return;
    if (dedupeId) seenMeetNotificationToastIds.add(dedupeId);
    if (typeof notify !== 'function') return;

    notify({
      title: String(payload?.toastTitle || 'Meet update').trim() || 'Meet update',
      body: String(payload?.toastBody || '').trim(),
      avatarUrl: String(payload?.memberAvatarUrl || '').trim() || defaultAvatarSrc,
      postBadge: false,
      linkPath: String(payload?.toastLinkPath || '/creator/meet').trim() || '/creator/meet',
      linkLabel: String(payload?.toastLinkLabel || 'View meeting').trim() || 'View meeting',
      variant: String(payload?.toastVariant || 'success').trim() || 'success',
      duration: 30000,
    });
    void fetchCreatorUnreadCount();
  });
  unsubscribeStorefrontOrderConfirmedToast = websocketStore.subscribe(
    'storefront:orderConfirmedToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = buildStorefrontOrderToastDedupeId(payload);
      if (dedupeId && seenStorefrontOrderToastIds.has(dedupeId)) return;
      if (dedupeId) seenStorefrontOrderToastIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      playStorefrontOrderConfirmedSound();

      const storefrontName = String(
        payload?.storefrontName ||
        payload?.storefront_name ||
        payload?.storeName ||
        payload?.store_name ||
        payload?.featureInstanceName ||
        payload?.instanceName ||
        payload?.storefrontTitle ||
        'Storefront'
      ).trim() || 'Storefront';
      const memberDisplayName = String(
        payload?.memberName || payload?.memberEmail || 'Member'
      ).trim() || 'Member';
      const orderNumber = String(payload?.orderNumber || '').trim();
      const title = orderNumber
        ? `${storefrontName} Order #${orderNumber} placed by ${memberDisplayName}`
        : `${storefrontName} Order placed by ${memberDisplayName}`;
      const body = '';
      const featureSlug = String(payload?.featureSlug || payload?.feature_slug || '').trim();
      const featureInstanceId = Number(payload?.featureInstanceId || payload?.feature_instance_id || 0);
      const stockroomOrdersPath = (() => {
        if (!featureSlug || !Number.isFinite(featureInstanceId) || featureInstanceId <= 0) return '';
        const encodedSlug = encodeURIComponent(featureSlug);
        const themeEditorPath = `/creator/theme-editor/${encodedSlug}/stockroom/orders?instanceId=${Math.trunc(featureInstanceId)}`;
        const creatorShellPath = `/creator/stockroom/${encodedSlug}/orders?instanceId=${Math.trunc(featureInstanceId)}`;
        return hasShellRouteAccess(themeEditorPath) ? themeEditorPath : creatorShellPath;
      })();
      const linkPath = String(
        stockroomOrdersPath || payload?.creatorLinkPath || payload?.toastLinkPath || ''
      ).trim();
      const linkLabel = String(payload?.toastLinkLabel || 'View order').trim();
      const avatarUrl = String(
        payload?.memberAvatarUrl || payload?.avatarUrl || defaultAvatarSrc
      ).trim();

      notify({
        title,
        body,
        avatarUrl,
        linkPath,
        linkLabel,
        postBadge: true,
        variant: 'success',
        duration: 45000,
      });
      void fetchCreatorUnreadCount();
    }
  );
  unsubscribeCryptoReceiveToast = websocketStore.subscribe(
    'crypto:receiveToast',
    (payload) => {
      if (!payload || typeof payload !== 'object') return;
      const dedupeId = buildCryptoReceiveToastDedupeId(payload);
      if (dedupeId && seenCryptoReceiveToastIds.has(dedupeId)) return;
      if (dedupeId) seenCryptoReceiveToastIds.add(dedupeId);
      if (typeof notify !== 'function') return;

      playStorefrontOrderConfirmedSound();

      const amountBtc = formatCryptoReceiveAmount(payload);
      notify({
        title: String(payload?.toastTitle || 'DeFi transaction success').trim(),
        body: String(payload?.toastBody || `You received ${amountBtc} BTC-Bitcoin`).trim(),
        avatarUrl: btcIconUrl,
        toastStyle: 'btc-receive',
        linkPath: String(payload?.toastLinkPath || '/creator/app-settings/crypto?section=tx').trim(),
        linkLabel: String(payload?.toastLinkLabel || 'See transaction').trim(),
        variant: 'success',
        duration: 45000,
      });
      void fetchCreatorUnreadCount();
    }
  );
  unsubscribeVideoChannelPublished = websocketStore.subscribe(
    'video-channel:videoPublished',
    (payload) => {
      if (!payload || typeof window === 'undefined') return;
      window.dispatchEvent(
        new CustomEvent('video-channel:video-published', { detail: payload })
      );
      void fetchCreatorUnreadCount();
    }
  );
  unsubscribeVideoChannelPublishedToast = websocketStore.subscribe(
    'video-channel:videoPublishedToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = buildVideoChannelToastDedupeId(payload);
      if (dedupeId && seenVideoChannelToastIds.has(dedupeId)) return;
      if (dedupeId) seenVideoChannelToastIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      const title = String(payload?.toastTitle || payload?.channelTitle || 'Video Channel').trim();
      const body = String(payload?.toastBody || '').trim();
      const linkPath = String(
        payload?.toastLinkPath || payload?.creatorLinkPath || ''
      ).trim();
      const linkLabel = String(payload?.toastLinkLabel || 'View video').trim();
      const avatarUrl = normalizeCreatorVideoChannelAvatarUrl(payload);

      notify({
        title,
        body,
        avatarUrl,
        linkPath,
        linkLabel,
        postBadge: true,
        variant: 'success',
        duration: 45000,
      });

      void fetchCreatorUnreadCount();
    }
  );
  unsubscribeFlixTheatrePublished = websocketStore.subscribe(
    'flix-theatre:videoPublished',
    (payload) => {
      if (!payload || typeof window === 'undefined') return;
      window.dispatchEvent(
        new CustomEvent('flix-theatre:video-published', { detail: payload })
      );
      void fetchCreatorUnreadCount();
    }
  );
  unsubscribeFlixTheatrePublishedToast = websocketStore.subscribe(
    'flix-theatre:videoPublishedToast',
    (payload) => {
      if (!payload) return;
      const dedupeId = buildFlixTheatreToastDedupeId(payload);
      if (dedupeId && seenFlixTheatreToastIds.has(dedupeId)) return;
      if (dedupeId) seenFlixTheatreToastIds.add(dedupeId);

      if (typeof notify !== 'function') return;

      const title = String(payload?.toastTitle || payload?.theatreTitle || 'Flix Theatre').trim();
      const body = String(payload?.toastBody || '').trim();
      const linkPath = String(
        payload?.toastLinkPath || payload?.creatorLinkPath || ''
      ).trim();
      const linkLabel = String(payload?.toastLinkLabel || 'Watch now').trim();
      const avatarUrl = normalizeCreatorFlixTheatreAvatarUrl(payload);

      notify({
        title,
        body,
        avatarUrl,
        toastStyle: 'flix-theatre-release',
        linkPath,
        linkLabel,
        postBadge: true,
        variant: 'success',
        duration: 45000,
      });

      void fetchCreatorUnreadCount();
    }
  );
  creatorUnreadSyncListener = () => {
    void fetchCreatorUnreadCount();
  };
  window.addEventListener('notifications:unread-sync', creatorUnreadSyncListener);
  creatorVisibilityListener = () => {
    if (document.visibilityState === 'visible') {
      void fetchCreatorUnreadCount();
    }
  };
  document.addEventListener('visibilitychange', creatorVisibilityListener);
});

onUnmounted(() => {
  platformInitiationRunId += 1;
  stopPlatformInitiationProgress();
  if (unsubscribeChatNewMessage) {
    unsubscribeChatNewMessage();
    unsubscribeChatNewMessage = null;
  }
if (unsubscribePresenceSnapshot) {
    unsubscribePresenceSnapshot();
    unsubscribePresenceSnapshot = null;
  }
  if (unsubscribePresenceUpdate) {
    unsubscribePresenceUpdate();
    unsubscribePresenceUpdate = null;
  }
  if (unsubscribeNewsfeedPostCreated) {
    unsubscribeNewsfeedPostCreated();
    unsubscribeNewsfeedPostCreated = null;
  }
  if (unsubscribeNewsfeedCommentToast) {
    unsubscribeNewsfeedCommentToast();
    unsubscribeNewsfeedCommentToast = null;
  }
  if (unsubscribeNewsfeedReplyToast) {
    unsubscribeNewsfeedReplyToast();
    unsubscribeNewsfeedReplyToast = null;
  }
  if (unsubscribeNewsfeedPostReactionToast) {
    unsubscribeNewsfeedPostReactionToast();
    unsubscribeNewsfeedPostReactionToast = null;
  }
  if (unsubscribeNewsfeedCommentReactionToast) {
    unsubscribeNewsfeedCommentReactionToast();
    unsubscribeNewsfeedCommentReactionToast = null;
  }
  if (unsubscribeMemberLifecycle) {
    unsubscribeMemberLifecycle();
    unsubscribeMemberLifecycle = null;
  }
  if (unsubscribeMemberJoinedToast) {
    unsubscribeMemberJoinedToast();
    unsubscribeMemberJoinedToast = null;
  }
  if (unsubscribeMemberSubscriptionToast) {
    unsubscribeMemberSubscriptionToast();
    unsubscribeMemberSubscriptionToast = null;
  }
  if (unsubscribeMeetNotificationToast) {
    unsubscribeMeetNotificationToast();
    unsubscribeMeetNotificationToast = null;
  }
  if (unsubscribeNotificationsRefresh) {
    unsubscribeNotificationsRefresh();
    unsubscribeNotificationsRefresh = null;
  }
  if (unsubscribeStorefrontOrderConfirmedToast) {
    unsubscribeStorefrontOrderConfirmedToast();
    unsubscribeStorefrontOrderConfirmedToast = null;
  }
  if (unsubscribeCryptoReceiveToast) {
    unsubscribeCryptoReceiveToast();
    unsubscribeCryptoReceiveToast = null;
  }
  if (unsubscribeVideoChannelPublished) {
    unsubscribeVideoChannelPublished();
    unsubscribeVideoChannelPublished = null;
  }
  if (unsubscribeVideoChannelPublishedToast) {
    unsubscribeVideoChannelPublishedToast();
    unsubscribeVideoChannelPublishedToast = null;
  }
  if (unsubscribeFlixTheatrePublished) {
    unsubscribeFlixTheatrePublished();
    unsubscribeFlixTheatrePublished = null;
  }
  if (unsubscribeFlixTheatrePublishedToast) {
    unsubscribeFlixTheatrePublishedToast();
    unsubscribeFlixTheatrePublishedToast = null;
  }
  if (creatorUnreadPollTimer) {
    clearInterval(creatorUnreadPollTimer);
    creatorUnreadPollTimer = null;
  }
  if (creatorUnreadSyncListener) {
    window.removeEventListener('notifications:unread-sync', creatorUnreadSyncListener);
    creatorUnreadSyncListener = null;
  }
  if (creatorVisibilityListener) {
    document.removeEventListener('visibilitychange', creatorVisibilityListener);
    creatorVisibilityListener = null;
  }
  if (creatorAuthExpiredListener) {
    window.removeEventListener(CREATOR_AUTH_EXPIRED_EVENT, creatorAuthExpiredListener);
    creatorAuthExpiredListener = null;
  }
});

// Call the function when the component is mounted
onMounted(() => {
  void loadShellSession();
  if (String(route.query.provisionPlatform || '') === '1') void resolvePlatformInitiationRequest();
});

watch(
  () => route.fullPath,
  () => {
    if (String(route.query.provisionPlatform || '') === '1') {
      void resolvePlatformInitiationRequest();
    }
    setTimeout(() => {
      updateCreatorBrowserTitle();
    }, 0);
  }
);

</script>

<style scoped>
.creator-shell-backdrop {
  min-height: 100vh;
  background-color: #fbfcff;
  background-image:
    radial-gradient(ellipse 440px 300px at 3% 16%, rgba(37, 203, 229, .18), transparent 76%),
    radial-gradient(ellipse 520px 360px at 47% 4%, rgba(115, 91, 230, .15), transparent 76%),
    radial-gradient(ellipse 440px 330px at 96% 45%, rgba(57, 153, 244, .15), transparent 76%),
    radial-gradient(ellipse 460px 320px at 30% 94%, rgba(160, 115, 231, .10), transparent 76%);
  background-attachment: fixed;
}
.creator-route-stage{position:relative;isolation:isolate}
.creator-page-view{position:relative;z-index:1;width:100%}

.platform-provisioning-screen {
  min-height: 510px;
  background:
    radial-gradient(circle at 50% 30%, rgba(89, 165, 255, 0.16), transparent 38%),
    linear-gradient(145deg, #ffffff 0%, #f7f9ff 54%, #f0fbff 100%);
}

.platform-provisioning-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(2px);
  opacity: 0.55;
  animation: platform-orb-drift 5s ease-in-out infinite alternate;
}

.platform-provisioning-orb--one {
  top: -95px;
  right: -75px;
  width: 240px;
  height: 240px;
  background: rgba(64, 210, 180, 0.18);
}

.platform-provisioning-orb--two {
  bottom: -110px;
  left: -80px;
  width: 270px;
  height: 270px;
  background: rgba(125, 77, 255, 0.14);
  animation-delay: -2.2s;
}

.platform-stack-stage {
  position: relative;
  width: 210px;
  height: 170px;
  animation: platform-stage-breathe 2.4s 1.2s ease-in-out infinite;
}

.platform-stack-glow {
  position: absolute;
  inset: 28px 24px 12px;
  border-radius: 50%;
  background: rgba(45, 140, 255, 0.26);
  filter: blur(24px);
  animation: platform-glow-pulse 1.8s ease-in-out infinite alternate;
}

.platform-stack-ring {
  position: absolute;
  inset: 7px 25px;
  border-radius: 50%;
  border: 1px solid rgba(45, 140, 255, 0.22);
  animation: platform-ring-expand 2.1s ease-out infinite;
}

.platform-stack-ring--two {
  animation-delay: 1.05s;
}

.platform-stack-layer {
  position: absolute;
  inset: 24px 9px 12px;
  opacity: 0;
  transform: translateY(-28px) scale(0.94);
  animation: platform-layer-arrive 750ms cubic-bezier(0.2, 0.9, 0.25, 1.18) forwards;
}

.platform-stack-layer img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 14px 13px rgba(24, 85, 190, 0.22));
}

.platform-stack-layer--bottom {
  clip-path: inset(56% 0 0 0);
  animation-delay: 60ms;
}

.platform-stack-layer--middle {
  clip-path: inset(28% 0 27% 0);
  animation-delay: 310ms;
}

.platform-stack-layer--top {
  clip-path: inset(0 0 55% 0);
  animation-delay: 560ms;
}

.platform-stack-stage--complete {
  animation: platform-ready-pop 650ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.platform-stack-stage--complete .platform-stack-ring {
  animation-play-state: paused;
  opacity: 0;
}

.platform-stack-check {
  position: absolute;
  top: 8px;
  right: 7px;
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border: 4px solid #ffffff;
  border-radius: 9999px;
  background: #16c79a;
  color: #ffffff;
  font-size: 22px;
  font-weight: 800;
  box-shadow: 0 8px 24px rgba(22, 199, 154, 0.3);
  animation: platform-check-in 420ms 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

@keyframes platform-layer-arrive {
  0% { opacity: 0; transform: translateY(-30px) scale(0.94); }
  64% { opacity: 1; }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes platform-stage-breathe {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
}

@keyframes platform-glow-pulse {
  from { opacity: 0.45; transform: scale(0.9); }
  to { opacity: 0.9; transform: scale(1.08); }
}

@keyframes platform-ring-expand {
  0% { opacity: 0; transform: scale(0.65); }
  22% { opacity: 0.75; }
  100% { opacity: 0; transform: scale(1.25); }
}

@keyframes platform-ready-pop {
  0% { transform: scale(0.94); }
  55% { transform: scale(1.06); }
  100% { transform: scale(1); }
}

@keyframes platform-check-in {
  from { opacity: 0; transform: scale(0.3) rotate(-25deg); }
  to { opacity: 1; transform: scale(1) rotate(0); }
}

@keyframes platform-orb-drift {
  from { transform: translate3d(-8px, -5px, 0) scale(0.95); }
  to { transform: translate3d(14px, 12px, 0) scale(1.08); }
}

@media (prefers-reduced-motion: reduce) {
  .platform-provisioning-orb,
  .platform-stack-stage,
  .platform-stack-glow,
  .platform-stack-ring,
  .platform-stack-layer,
  .platform-stack-check {
    animation-duration: 1ms !important;
    animation-delay: 0ms !important;
    animation-iteration-count: 1 !important;
  }
}

/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>

  
